// frontend/src/pages/dashboard/CalendarPage.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { reportAPI } from '../../services/api';
import realtime from '../../hooks/RealtimePoll';
import { useAuth } from '../../context/AuthContext';
import { convertToCompanyTime } from '../../utils/time';
import { pollingManager } from '../../hooks/RealtimePoll';
import {
  Box, Typography, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Snackbar, Alert,
  Chip, List, ListItem, ListItemText, IconButton,
  useMediaQuery, useTheme, Paper, Divider, ToggleButtonGroup,
  ToggleButton, Tooltip, Skeleton, Stack, TextField, InputAdornment,
} from '@mui/material';
import {
  Close, CalendarToday, ViewModule, ViewWeek, ViewDay,
  Today, AccessTime, Person, Work, FilterList, Refresh,
  Folder, Event, CalendarMonth, NavigateBefore, NavigateNext,
  CheckCircle, ArrowBack, ArrowForward, DateRange, Clear
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment-timezone';

// ─────────────────────────────────────────────────────────────
// 🎨 Priority Color Mapping
// ─────────────────────────────────────────────────────────────
const DEFAULT_PRIORITY_COLORS = {
  low: '#4caf50',
  medium: '#ff9800',
  high: '#f44336',
  urgent: '#c62828',
  critical: '#9c27b0',
};

const PRIORITY_ICONS = {
  low: '🟢',
  medium: '🟡',
  high: '🔴',
  urgent: '⚡',
  critical: '💎',
};

const spinAnimation = '@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }';

// ─────────────────────────────────────────────────────────────
// 🧩 Helper Functions - FIXED TIMEZONE HANDLING
// ─────────────────────────────────────────────────────────────

/**
 * Normalize date to start of day in company timezone
 * FIXED: Properly handles timezone conversion
 */
const normalizeToDayStart = (date, timezone) => {
  if (!date) return null;

  // If it's already a moment object, use it
  let m = moment.isMoment(date) ? date.clone() : moment(date);

  // Convert to company timezone and set to start of day
  return m.tz(timezone).startOf('day');
};

/**
 * Create a date in company timezone from year/month/day
 * FIXED: Ensures we create the date in the correct timezone
 */
const createDateInTimezone = (year, month, day, timezone) => {
  // Create date string in YYYY-MM-DD format
  const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  // Parse it in the company timezone
  return moment.tz(dateStr, 'YYYY-MM-DD', timezone).startOf('day');
};

/**
 * Check if event's start OR end date falls on the selected day
 * FIXED: Properly compares dates in company timezone
 */
const isEventOnSelectedDay = (event, selectedDay, timezone) => {
  if (!selectedDay) return true;

  const isTask = event.extendedProps?.type === 'task';
  const selectedDate = normalizeToDayStart(selectedDay, timezone);

  if (isTask) {
    const dueDate = normalizeToDayStart(event.start, timezone);
    return dueDate.isSame(selectedDate, 'day');
  } else {
    const eventStart = normalizeToDayStart(event.start, timezone);
    const eventEnd = event.end ? normalizeToDayStart(event.end, timezone) : eventStart;

    // Show if: starts on day OR ends on day OR within range
    const startsOnDay = eventStart.isSame(selectedDate, 'day');
    const endsOnDay = eventEnd.isSame(selectedDate, 'day');
    const withinRange = eventStart.isBefore(selectedDate, 'day') && eventEnd.isAfter(selectedDate, 'day');

    return startsOnDay || endsOnDay || withinRange;
  }
};

/**
 * Determine event's relationship to selected date
 * FIXED: Properly handles timezone for start/end detection
 */
const getEventDateRelation = (event, selectedDate, timezone) => {
  if (!selectedDate) return null;

  const isTask = event.extendedProps?.type === 'task';
  const checkDate = normalizeToDayStart(selectedDate, timezone);
  const today = normalizeToDayStart(moment(), timezone);

  if (isTask) {
    // For tasks: start = due date (treat as END)
    const dueDate = normalizeToDayStart(event.start, timezone);
    const isDue = dueDate.isSame(checkDate, 'day');
    const isOverdue = dueDate.isBefore(today, 'day') && event.extendedProps?.status !== 'completed';

    if (isOverdue && isDue) return 'overdue';
    if (isDue) return 'end';
    if (dueDate.isBefore(today, 'day') && event.extendedProps?.status !== 'completed') return 'overdue';

    return null;
  } else {
    // For projects: start = start date, end = end date
    const eventStart = normalizeToDayStart(event.start, timezone);
    const eventEnd = event.end ? normalizeToDayStart(event.end, timezone) : null;

    const isStart = eventStart.isSame(checkDate, 'day');
    const isEnd = eventEnd ? eventEnd.isSame(checkDate, 'day') : false;
    const isStarted = eventStart.isBefore(today, 'day');
    const isOverdue = eventEnd && eventEnd.isBefore(today, 'day') && event.extendedProps?.status !== 'completed';

    // Priority order: overdue > end > start > started
    if (isOverdue && isEnd) return 'overdue';
    if (isOverdue) return 'overdue';
    if (isStart && isEnd) return 'both';
    if (isStart) return 'start';
    if (isEnd) return 'end';
    if (isStarted && eventStart.isBefore(checkDate, 'day')) return 'started';
    if (eventStart.isBefore(checkDate, 'day') && eventEnd && eventEnd.isAfter(checkDate, 'day')) {
      return 'within';
    }

    return null;
  }
};

const extractUniquePriorities = (events) => {
  if (!Array.isArray(events)) return [];

  const taskPriorities = events
    .filter(event => event.extendedProps?.type === 'task')
    .map(event => event.extendedProps?.priority)
    .filter(priority => priority && typeof priority === 'string');

  const unique = [...new Set(taskPriorities.map(p => p.toLowerCase()))];
  const priorityOrder = ['critical', 'urgent', 'high', 'medium', 'low'];
  return unique.sort((a, b) => {
    const indexA = priorityOrder.indexOf(a);
    const indexB = priorityOrder.indexOf(b);
    if (indexA === -1 && indexB === -1) return a.localeCompare(b);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
};

const getPriorityColor = (priority) => {
  if (!priority) return DEFAULT_PRIORITY_COLORS.medium;
  const normalized = priority.toLowerCase().replace(/\s+/g, '');
  return DEFAULT_PRIORITY_COLORS[normalized] || DEFAULT_PRIORITY_COLORS.medium;
};

const getPriorityIcon = (priority) => {
  if (!priority) return PRIORITY_ICONS.medium;
  const normalized = priority.toLowerCase().replace(/\s+/g, '');
  return PRIORITY_ICONS[normalized] || PRIORITY_ICONS.medium;
};

const isTaskEvent = (event) => event.extendedProps?.type === 'task';

const getViewIcon = (view) => {
  switch (view) {
    case 'dayGridMonth': return <ViewModule />;
    case 'dayGridWeek': return <ViewWeek />;
    case 'dayGridDay': return <ViewDay />;
    default: return <ViewModule />;
  }
};

// ─────────────────────────────────────────────────────────────
// 🧩 Reusable Sub-Components
// ─────────────────────────────────────────────────────────────

const EventContent = React.memo(({ event, selectedDate, timezone, currentView }) => {
  const { priority, type, status } = event.extendedProps || {};
  const isTask = type === 'task';
  const priorityColor = getPriorityColor(priority);
  const priorityIcon = getPriorityIcon(priority);
  const typeIcon = isTask ? '📋' : '📁';

  const dateRelation = (currentView === 'dayGridDay' && selectedDate)
    ? getEventDateRelation(event, selectedDate, timezone)
    : null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', overflow: 'hidden' }}>
      {/* Priority indicator for tasks */}
      {isTask && priority && (
        <Box sx={{ width: 3, height: '100%', bgcolor: priorityColor, borderRadius: '2px', flexShrink: 0 }} />
      )}

      {/* Date badges */}
      {dateRelation && dateRelation !== 'within' && (
        <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
          {/* Started badge (past start date) */}
          {dateRelation === 'started' && (
            <Chip
              label="Started"
              size="small"
              sx={{
                height: 16,
                minWidth: 16,
                bgcolor: 'info.main',
                color: 'info.contrastText',
                '& .MuiChip-label': { px: 0.3, py: 0, fontSize: '0.6rem', fontWeight: 700 },
                borderRadius: '4px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}
              title="Started"
            />
          )}

          {/* Start badge (today) */}
          {!isTask && (dateRelation === 'start' || dateRelation === 'both') && (
            <Chip
              label="Starting"
              size="small"
              color="success"
              variant="filled"
              sx={{
                height: 16,
                minWidth: 16,
                '& .MuiChip-label': { px: 0.4, py: 0, fontSize: '0.65rem', fontWeight: 700 },
                borderRadius: '4px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}
              title="Start date"
            />
          )}

          {/* End/Due badge */}
          {(dateRelation === 'end' || dateRelation === 'both') && (
            <Chip
              label="Ending"
              size="small"
              color="error"
              variant="filled"
              sx={{
                height: 16,
                minWidth: 16,
                '& .MuiChip-label': { px: 0.4, py: 0, fontSize: '0.65rem', fontWeight: 700 },
                borderRadius: '4px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}
              title={isTask ? "Due date" : "End date"}
            />
          )}

          {/* Overdue badge */}
          {dateRelation === 'overdue' && (
            <Chip
              label="Backlog"
              size="small"
              sx={{
                height: 16,
                minWidth: 16,
                bgcolor: 'warning.main',
                color: 'warning.contrastText',
                '& .MuiChip-label': { px: 0.3, py: 0, fontSize: '0.6rem', fontWeight: 700 },
                borderRadius: '4px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}
              title="Overdue"
            />
          )}
        </Box>
      )}

      {/* Event title */}
      <Typography variant="caption" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
        {typeIcon} {event.title}
      </Typography>

      {/* Priority icon for tasks */}
      {isTask && priority && (
        <Box sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.9)', flexShrink: 0 }}>{priorityIcon}</Box>
      )}
    </Box>
  );
});
EventContent.displayName = 'EventContent';

const SelectionBar = React.memo(({ selectedDate, onClear, timezone, currentView }) => {
  if (!selectedDate || currentView !== 'dayGridDay') return null;

  const formattedDate = moment.tz(selectedDate, timezone).format('dddd, MMMM D, YYYY');

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'primary.50', borderLeft: 4, borderColor: 'primary.main' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DateRange color="primary" />
        <Box>
          <Typography variant="body2" color="text.secondary">Showing events for:</Typography>
          <Typography variant="subtitle1" fontWeight={600}>{formattedDate}</Typography>
        </Box>
      </Box>
      <IconButton onClick={onClear} size="small" color="primary" sx={{ ml: 1 }}><Clear fontSize="small" /></IconButton>
    </Paper>
  );
});
SelectionBar.displayName = 'SelectionBar';

const EventDetailModal = React.memo(({ event, open, onClose, timezone, selectedDate, currentView }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!event) return null;

  const { extendedProps } = event;
  const isProject = extendedProps?.type === 'project';
  const priority = extendedProps?.priority;
  const priorityColor = getPriorityColor(priority);
  const priorityIcon = getPriorityIcon(priority);

  const dateRelation = (currentView === 'dayGridDay' && selectedDate)
    ? getEventDateRelation(event, selectedDate, timezone)
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile} sx={{ '& .MuiDialog-paper': { borderRadius: 3 } }}>
      <DialogTitle sx={{ background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', p: 2.5 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="h6" fontWeight="bold">
              {isProject ? <Folder sx={{ verticalAlign: 'middle', mr: 0.5 }} /> : <Event sx={{ verticalAlign: 'middle', mr: 0.5 }} />}
              {event.title}
            </Typography>
            {!isProject && priority && (
              <Chip label={`${priorityIcon} ${priority}`} size="small" sx={{ bgcolor: priorityColor, color: '#fff', fontWeight: 600, height: 24, '& .MuiChip-label': { px: 1 } }} />
            )}
          </Box>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>{isProject ? 'Project Milestone' : 'Task Deadline'}</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}><Close /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <List sx={{ p: 1 }}>
          {dateRelation && dateRelation !== 'within' && (
            <>
              <ListItem sx={{ bgcolor: 'action.hover' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {dateRelation === 'started' && (
                      <Chip
                        icon={<CheckCircle fontSize="small" />}
                        label="Started"
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                    {(dateRelation === 'start' || dateRelation === 'both') && (
                      <Chip
                        icon={<ArrowBack fontSize="small" />}
                        label="Starts"
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                    {(dateRelation === 'end' || dateRelation === 'both') && (
                      <Chip
                        icon={<ArrowForward fontSize="small" />}
                        label={isProject ? "Ends" : "Due"}
                        size="small"
                        color="error"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                    {dateRelation === 'overdue' && (
                      <Chip
                        icon={<AccessTime fontSize="small" />}
                        label="Overdue"
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {dateRelation === 'started' ? 'project is active' : 'on selected date'}
                  </Typography>
                </Box>
              </ListItem>
              <Divider />
            </>
          )}

          {!isProject && (
            <>
              <ListItem>
                <ListItemText primary={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Work sx={{ fontSize: 18, color: 'text.secondary' }} /><Typography variant="body2" fontWeight={500}>Project</Typography></Box>} secondary={extendedProps.projectName || '—'} />
              </ListItem>
              <Divider />
            </>
          )}

          <ListItem>
            <ListItemText primary={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Person sx={{ fontSize: 18, color: 'text.secondary' }} /><Typography variant="body2" fontWeight={500}>{isProject ? 'Team' : 'Assignee'}</Typography></Box>} secondary={extendedProps.assignees || extendedProps.assignee || 'Unassigned'} />
          </ListItem>
          <Divider />

          <ListItem>
            <ListItemText primary={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><AccessTime sx={{ fontSize: 18, color: 'text.secondary' }} /><Typography variant="body2" fontWeight={500}>{isProject ? 'Timeline' : 'Due Date'}</Typography></Box>} secondary={extendedProps[isProject ? 'startDate' : 'dueDate'] ? convertToCompanyTime(extendedProps[isProject ? 'startDate' : 'dueDate'], timezone) : '—'} />
          </ListItem>
          <Divider />

          {isProject && extendedProps.endDate && (
            <>
              <ListItem><ListItemText primary={<Typography variant="body2" fontWeight={500}>End Date</Typography>} secondary={convertToCompanyTime(extendedProps.endDate, timezone)} /></ListItem>
              <Divider />
            </>
          )}

          {!isProject && (
            <>
              {priority && (
                <>
                  <ListItem><ListItemText primary={<Typography variant="body2" fontWeight={500}>Priority</Typography>} secondary={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: priorityColor }} /><Typography variant="body2">{priority}</Typography></Box>} /></ListItem>
                  <Divider />
                </>
              )}
              <ListItem><ListItemText primary={<Typography variant="body2" fontWeight={500}>Time Spent</Typography>} secondary={extendedProps.totalTimeDisplay || '0m'} /></ListItem>
              <Divider />
            </>
          )}

          <ListItem><ListItemText primary={<Typography variant="body2" fontWeight={500}>Status</Typography>} secondary={<Chip label={extendedProps.status || '—'} size="small" color={extendedProps.status === 'completed' ? 'success' : extendedProps.status === 'in_progress' ? 'primary' : extendedProps.status === 'blocked' ? 'error' : 'default'} sx={{ fontWeight: 500 }} />} /></ListItem>
          <Divider />

          {extendedProps.description && (
            <ListItem alignItems="flex-start"><ListItemText primary={<Typography variant="body2" fontWeight={500}>Description</Typography>} secondary={<Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>{extendedProps.description}</Typography>} /></ListItem>
          )}
        </List>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" fullWidth>Close</Button>
        {!isProject && extendedProps.status !== 'completed' && (
          <Button variant="contained" color="primary" fullWidth><CheckCircle sx={{ mr: 0.5, fontSize: 18 }} /> Mark Complete</Button>
        )}
      </DialogActions>
    </Dialog>
  );
});
EventDetailModal.displayName = 'EventDetailModal';

const CalendarStats = React.memo(({ events, selectedDate, timezone, currentView }) => {
  const stats = useMemo(() => {
    const today = normalizeToDayStart(new Date(), timezone);
    const taskEvents = events.filter(e => isTaskEvent(e));

    const filteredForStats = (currentView === 'dayGridDay' && selectedDate)
      ? events.filter(e => isEventOnSelectedDay(e, selectedDate, timezone))
      : events;

    const filteredTasks = filteredForStats.filter(e => isTaskEvent(e));

    return {
      total: filteredForStats.length,
      tasks: filteredTasks.length,
      projects: filteredForStats.length - filteredTasks.length,
      today: filteredForStats.filter(e => normalizeToDayStart(e.start, timezone).isSame(today, 'day')).length,
      highPriority: filteredTasks.filter(e => ['high', 'urgent', 'critical'].includes(e.extendedProps?.priority?.toLowerCase())).length,
      overdue: filteredForStats.filter(e => {
        const end = e.end ? normalizeToDayStart(e.end, timezone) : normalizeToDayStart(e.start, timezone);
        return end.isBefore(today) && e.extendedProps?.status !== 'completed';
      }).length
    };
  }, [events, selectedDate, timezone, currentView]);

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, background: 'linear-gradient(135deg, rgba(63,81,181,0.08), rgba(33,150,243,0.08))' }}>
      <Stack direction={{ xs: 'row' }} spacing={2} justifyContent={{ xs: 'space-between', sm: 'space-around' }} flexWrap={{ xs: 'wrap' }}>
        {[
          { label: 'Total Events', value: stats.total, color: 'primary' },
          { label: 'Tasks', value: stats.tasks, color: 'info' },
          { label: 'Projects', value: stats.projects, color: 'secondary' },
          { label: 'Today', value: stats.today, color: 'success' },
          { label: 'High Priority', value: stats.highPriority, color: 'warning' },
          { label: 'Overdue', value: stats.overdue, color: 'error' },
        ].map((stat) => (
          <Box key={stat.label} textAlign="center">
            <Typography variant="h4" fontWeight="bold" fontSize={{ xs: '20px', sm: '40px' }} color={`${stat.color}.main`}>{stat.value}</Typography>
            <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
});
CalendarStats.displayName = 'CalendarStats';

const PriorityFilter = React.memo(({ priorities, selected, onChange }) => {
  if (!priorities || priorities.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <FilterList fontSize="small" color="action" />
      <Typography variant="body2" fontWeight={500}>Task Priority:</Typography>
      <ToggleButtonGroup value={selected} exclusive onChange={onChange} size="small" sx={{ height: '27px' }}>
        <ToggleButton value="all" size="small">All</ToggleButton>
        {priorities.map((priority) => {
          const color = getPriorityColor(priority);
          const icon = getPriorityIcon(priority);
          return (
            <ToggleButton key={priority} value={priority} size="small" sx={{ '&.Mui-selected': { bgcolor: `${color} !important`, color: 'white !important' } }}>
              {icon} {priority.charAt(0).toUpperCase() + priority.slice(1)}
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>
    </Box>
  );
});
PriorityFilter.displayName = 'PriorityFilter';

// ─────────────────────────────────────────────────────────────
// 🎯 Main Component - FIXED TIMEZONE ISSUE
// ─────────────────────────────────────────────────────────────
export default function CalendarPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const [currentView, setCurrentView] = useState('dayGridDay');
  const [selectedDate, setSelectedDate] = useState(null);
  const [jumpToDate, setJumpToDate] = useState(moment());
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const calendarRef = useRef(null);
  const isUserActionRef = useRef(false);
  const isInitializedRef = useRef(false);

  const [filterPriority, setFilterPriority] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const { company } = useAuth();
  const timezone = company?.timezone || 'UTC';

  // Initialize to today ONCE
  useEffect(() => {
    if (!isInitializedRef.current && timezone && calendarRef.current) {
      const today = normalizeToDayStart(moment(), timezone);
      setSelectedDate(today);
      setJumpToDate(today);

      const api = calendarRef.current.getApi();
      api.changeView('dayGridDay');
      api.gotoDate(today.toDate());

      isInitializedRef.current = true;
    }
  }, [timezone]);

  const availablePriorities = useMemo(() => extractUniquePriorities(events), [events]);

  const filteredEvents = useMemo(() => {
    return events.filter(event => {
      const eventType = event.extendedProps?.type;

      if (filterType !== 'all' && eventType !== filterType) return false;

      if (filterType !== 'project' && filterPriority !== 'all' && eventType === 'task') {
        const eventPriority = event.extendedProps?.priority?.toLowerCase();
        if (eventPriority !== filterPriority.toLowerCase()) return false;
      }

      if (currentView === 'dayGridDay' && selectedDate) {
        if (!isEventOnSelectedDay(event, selectedDate, timezone)) return false;
      }

      return true;
    });
  }, [events, filterType, filterPriority, selectedDate, timezone, currentView]);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportAPI.getCalendarEvents();
      const payload = Array.isArray(res?.data) ? res.data : [];

      const mapped = payload.map((e) => ({
        id: String(e.id),
        title: e.title,
        start: e.start,
        end: e.end || null,
        backgroundColor: e.color || undefined,
        extendedProps: { ...e, ...(e.extendedProps || {}) },
      }));

      setEvents(mapped);
    } catch (err) {
      console.error('Failed to load events', err);
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to load calendar events', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEvents();
    realtime.start('tasks', {}, null, 30000);
    realtime.start('projects', {}, null, 30000);
    const refresh = () => loadEvents();
    realtime.on('tasks', refresh);
    realtime.on('projects', refresh);
    return () => {
      realtime.off('tasks', refresh);
      realtime.off('projects', refresh);
      realtime.stop('tasks');
      realtime.stop('projects');
    };
  }, [loadEvents]);

  const handleEventClick = useCallback((clickInfo) => {
    const found = events.find((e) => String(e.id) === String(clickInfo.event.id));
    if (found) {
      setSelectedEvent(found);
      setIsModalOpen(true);
    }
  }, [events]);

  // FIXED: Properly handle timezone when clicking a date
  const handleDateClick = useCallback((clickInfo) => {
    const { date, dateStr, allDay } = clickInfo;

    // For state management: normalize to company timezone start of day
    const clickedDate = allDay && dateStr
      ? moment.tz(dateStr, 'YYYY-MM-DD', timezone).startOf('day')
      : normalizeToDayStart(date, timezone);

    isUserActionRef.current = true;
    setCurrentView('dayGridDay');
    setSelectedDate(clickedDate);
    setJumpToDate(clickedDate);

    if (calendarRef.current) {
      const api = calendarRef.current.getApi();
      api.changeView('dayGridDay');

      setTimeout(() => {
        // ✅ FIX: Use FullCalendar's original date object
        // Since calendar has timeZone set, FC handles conversion correctly
        // Avoid .toDate() conversion that causes UTC drift
        api.gotoDate(date);

        setTimeout(() => {
          isUserActionRef.current = false;
        }, 100);
      }, 50);
    }
  }, [timezone]);

  const handleViewChange = useCallback((_, newView) => {
    if (!newView || !calendarRef.current) return;

    isUserActionRef.current = true;

    setCurrentView(newView);
    calendarRef.current.getApi().changeView(newView);

    if (newView !== 'dayGridDay') {
      setSelectedDate(null);
    } else {
      const currentDate = normalizeToDayStart(calendarRef.current.getApi().getDate(), timezone);
      setSelectedDate(currentDate);
      setJumpToDate(currentDate);
    }

    setTimeout(() => {
      isUserActionRef.current = false;
    }, 100);
  }, [timezone]);

  const handleToday = useCallback(() => {
    if (!calendarRef.current) return;

    isUserActionRef.current = true;
    const today = normalizeToDayStart(moment(), timezone);

    setCurrentView('dayGridDay');
    setSelectedDate(today);
    setJumpToDate(today);

    const api = calendarRef.current.getApi();
    api.changeView('dayGridDay');

    // ✅ FIX: Use ISO string format for consistent timezone handling
    api.gotoDate(today.format('YYYY-MM-DD'));

    setTimeout(() => {
      isUserActionRef.current = false;
    }, 100);
  }, [timezone]);

  const handlePrev = useCallback(() => {
    if (!calendarRef.current) return;

    calendarRef.current.getApi().prev();

    if (currentView === 'dayGridDay' && timezone) {
      const newDate = normalizeToDayStart(calendarRef.current.getApi().getDate(), timezone);
      setSelectedDate(newDate);
      setJumpToDate(newDate);
    }
  }, [currentView, timezone]);

  const handleNext = useCallback(() => {
    if (!calendarRef.current) return;

    calendarRef.current.getApi().next();

    if (currentView === 'dayGridDay' && timezone) {
      const newDate = normalizeToDayStart(calendarRef.current.getApi().getDate(), timezone);
      setSelectedDate(newDate);
      setJumpToDate(newDate);
    }
  }, [currentView, timezone]);

  const handleDateChange = useCallback((newDate) => {
    if (!newDate || !calendarRef.current) return;

    isUserActionRef.current = true;
    const momentDate = normalizeToDayStart(
      moment.isMoment(newDate) ? newDate : moment(newDate),
      timezone
    );

    setCurrentView('dayGridDay');
    setSelectedDate(momentDate);
    setJumpToDate(momentDate);

    const api = calendarRef.current.getApi();
    api.changeView('dayGridDay');

    // ✅ FIX: Pass ISO string for all-day views to avoid timezone drift
    setTimeout(() => {
      api.gotoDate(momentDate.format('YYYY-MM-DD'));
      setTimeout(() => {
        isUserActionRef.current = false;
      }, 100);
    }, 50);
  }, [timezone]);

  const handleFilterChange = useCallback((_, newFilter) => {
    if (newFilter !== null) setFilterPriority(newFilter);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  }, []);

  const handleSnackbarClose = useCallback(() => {
    setSnackbar((s) => ({ ...s, open: false }));
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedDate(null);
  }, []);

  const handleDatesSet = useCallback((dateInfo) => {
    if (!isUserActionRef.current) return;

    const newDate = normalizeToDayStart(dateInfo.start, timezone);

    if (currentView === 'dayGridDay') {
      setSelectedDate(newDate);
      setJumpToDate(newDate);
    }
  }, [currentView, timezone]);

  const calendarOptions = useMemo(() => ({
    timeZone: timezone,
    plugins: [dayGridPlugin, interactionPlugin],
    initialView: currentView,
    events: filteredEvents,
    height: isMobile ? 'auto' : '700px',
    eventClick: handleEventClick,
    dateClick: handleDateClick,
    selectable: false,
    eventContent: (arg) => <EventContent event={arg.event} selectedDate={selectedDate} timezone={timezone} currentView={currentView} />,
    headerToolbar: false,
    dayMaxEventRows: isMobile ? 3 : 6,
    views: {
      dayGridMonth: { dayMaxEventRows: isMobile ? 2 : 4, titleFormat: { year: 'numeric', month: 'long' } },
      dayGridWeek: { titleFormat: { year: 'numeric', month: 'short', day: 'numeric' } },
      dayGridDay: {
        titleFormat: { year: 'numeric', month: 'short', day: 'numeric' },
        dayMaxEventRows: null,
        slotLabelFormat: { hour: '2-digit', minute: '2-digit', hour12: true }
      }
    },
    eventClassNames: (arg) => {
      const priority = arg.event.extendedProps?.priority;
      const type = arg.event.extendedProps?.type;
      const classes = [];
      if (type === 'task' && priority) {
        const normalized = priority.toLowerCase().replace(/\s+/g, '-');
        classes.push(`fc-event-priority-${normalized}`);
      }
      return classes;
    },
    weekends: true,
    editable: false,
    eventLongPressDelay: isMobile ? 300 : 1000,
    allDaySlot: true,
    slotMinTime: '00:00:00',
    slotMaxTime: '24:00:00',
    datesSet: handleDatesSet,
    eventDidMount: (info) => {
      if (info.event.extendedProps.type === 'task') {
        info.el.setAttribute('data-task-priority', info.event.extendedProps.priority || 'medium');
      }
    }
  }), [timezone, currentView, filteredEvents, isMobile, handleEventClick, handleDateClick, selectedDate, handleDatesSet]);

  return (
    <DashboardLayout>
      <Box sx={{ flexGrow: 1, position: 'relative', overflowX: 'auto', p: { xs: 1, sm: 2, md: 3 }, pb: { xs: 8, sm: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
          <Box>
            <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" gutterBottom>
              <CalendarToday sx={{ verticalAlign: 'middle', mr: 1 }} />
              Task & Project Calendar
            </Typography>
            <Typography variant="body2" color="text.secondary">Plan, track, and manage your deadlines in one view</Typography>
          </Box>
        </Box>

        <SelectionBar selectedDate={selectedDate} onClear={handleClearSelection} timezone={timezone} currentView={currentView} />

        {!loading && (
          <Box sx={{ mb: { xs: 2, sm: 3 }, overflowX: { xs: 'auto', sm: 'visible' } }}>
            <CalendarStats events={events} selectedDate={selectedDate} timezone={timezone} currentView={currentView} />
          </Box>
        )}

        <Box sx={{ mb: 1, display: 'flex', gap: { xs: 0.5, sm: 1 }, alignItems: 'center', flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'space-between' }, flexDirection: { xs: 'column-reverse', md: 'row' } }}>
          {!loading && availablePriorities.length > 0 && filterType !== 'project' && (
            <PriorityFilter priorities={availablePriorities} selected={filterPriority} onChange={handleFilterChange} />
          )}

          <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, alignItems: 'center', flexWrap: 'wrap', justifyContent: { xs: 'center', sm: 'center' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 0.5 } }}>
              <Tooltip title="Previous"><IconButton onClick={handlePrev} size="small"><NavigateBefore /></IconButton></Tooltip>
              <Tooltip title="Next"><IconButton onClick={handleNext} size="small"><NavigateNext /></IconButton></Tooltip>
            </Box>

            <ToggleButtonGroup value={currentView} exclusive onChange={handleViewChange} size="small" sx={{ '& .MuiToggleButton-root': { px: { xs: 1, sm: 1.5 }, py: 0.5, minWidth: { xs: '36px', sm: 'auto' } }, height: { xs: '32px', sm: '27px' } }}>
              {['dayGridMonth', 'dayGridWeek', 'dayGridDay'].map((view) => (
                <ToggleButton key={view} value={view} aria-label={view}>
                  <Tooltip title={view.replace('dayGrid', '').replace(/([A-Z])/g, ' $1').trim()}>{getViewIcon(view)}</Tooltip>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <ToggleButtonGroup value={filterType} exclusive onChange={(_, newType) => { if (newType !== null) setFilterType(newType); }} size="small" sx={{ '& .MuiToggleButton-root': { px: { xs: 1, sm: 1.5 }, py: 0.5, minWidth: { xs: '36px', sm: 'auto' } }, height: { xs: '32px', sm: '27px' } }}>
              <ToggleButton value="all" size="small"><Tooltip title="Show Tasks & Projects"><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Event sx={{ fontSize: 18 }} /><Folder sx={{ fontSize: 18 }} /></Box></Tooltip></ToggleButton>
              <ToggleButton value="task" size="small"><Tooltip title="Show Tasks Only"><Event sx={{ fontSize: 18 }} /></Tooltip></ToggleButton>
              <ToggleButton value="project" size="small"><Tooltip title="Show Projects Only"><Folder sx={{ fontSize: 18 }} /></Tooltip></ToggleButton>
            </ToggleButtonGroup>

            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker value={jumpToDate} onChange={handleDateChange} slotProps={{
                textField: { size: 'small', placeholder: 'Jump to date', InputProps: { startAdornment: <InputAdornment position="start" sx={{ ml: 0.5 }}><CalendarMonth fontSize="small" color="action" /></InputAdornment> }, sx: { width: { xs: 180, sm: 180 }, minWidth: { xs: '140px', sm: 180 }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' } } }
              }} />
            </LocalizationProvider>

            <Tooltip title="Jump to today"><IconButton onClick={handleToday} size={isMobile ? 'medium' : 'small'} color="primary" sx={{ '& svg': { fontSize: { xs: 20, sm: 24 } } }}><Today /></IconButton></Tooltip>

            <Tooltip title="Refresh events"><IconButton onClick={loadEvents} size="small" disabled={loading} sx={{ ...(loading && { animation: 'spin 1s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }) }}><Refresh /></IconButton></Tooltip>
          </Box>
        </Box>

        {loading ? (
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', p: 3 }}>
            <Stack spacing={2}>
              <Skeleton variant="rectangular" height={40} />
              {[...Array(6)].map((_, i) => <Skeleton key={i} variant="rectangular" height={100} />)}
            </Stack>
          </Paper>
        ) : (
          <Paper variant="outlined" sx={{
            borderRadius: 2, overflow: 'hidden', mb: 3,
            '& .fc': { '--fc-border-color': theme.palette.divider, '--fc-page-bg-color': theme.palette.background.paper, '--fc-neutral-bg-color': theme.palette.action.hover, '--fc-list-event-hover-bg-color': theme.palette.action.selected, '--fc-today-bg-color': `${theme.palette.primary.main}15`, fontFamily: theme.typography.fontFamily },
            '& .fc-toolbar-title': { fontSize: isMobile ? '1.1rem' : '1.25rem', fontWeight: 600 },
            '& .fc-button': { textTransform: 'none', borderRadius: 1, fontWeight: 500 },
            '& .fc-button-primary': { bgcolor: theme.palette.primary.main, borderColor: theme.palette.primary.main, '&:hover': { bgcolor: theme.palette.primary.dark } },
            '& .fc-event': { borderRadius: 1, border: 'none', boxShadow: theme.shadows[1], transition: 'transform 0.15s ease, box-shadow 0.15s ease', '&:hover': { transform: 'translateY(-1px)', boxShadow: theme.shadows[3], zIndex: 10 } },
            '& .fc-daygrid-day': { cursor: 'pointer', transition: 'background-color 0.15s ease', '&:hover': { bgcolor: `${theme.palette.primary.main}10` } },
            '& .fc-daygrid-day.fc-day-today': { '&:hover': { bgcolor: `${theme.palette.primary.main}20` } },
            ...availablePriorities.reduce((acc, priority) => {
              const color = getPriorityColor(priority);
              const normalized = priority.toLowerCase().replace(/\s+/g, '-');
              return { ...acc, [`& .fc-event-priority-${normalized}`]: { borderLeft: `3px solid ${color} !important` } };
            }, {}),
          }}>
            <Box sx={{ p: { xs: 1, sm: 2 } }}>
              <FullCalendar ref={calendarRef} {...calendarOptions} />
            </Box>

            {availablePriorities.length > 0 && (
              <Box sx={{ p: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: { xs: 'center', sm: 'flex-start' }, borderTop: `1px solid ${theme.palette.divider}`, bgcolor: theme.palette.background.default }}>
                <Typography variant="body2" sx={{ mr: 1, fontWeight: 500 }}>Task Priority:</Typography>
                {availablePriorities.map((priority) => {
                  const color = getPriorityColor(priority);
                  const icon = getPriorityIcon(priority);
                  return (
                    <Chip key={priority} label={`${icon} ${priority.charAt(0).toUpperCase() + priority.slice(1)}`} size="small" onClick={() => setFilterPriority(priority)} sx={{ bgcolor: filterPriority === priority ? color : `${color}20`, color: filterPriority === priority ? '#fff' : color, cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { bgcolor: color, color: '#fff', transform: 'scale(1.05)' } }} />
                  );
                })}
              </Box>
            )}

            <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', justifyContent: { xs: 'center', sm: 'flex-start' }, borderTop: `1px solid ${theme.palette.divider}`, bgcolor: theme.palette.background.default }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Date Indicators:</Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Chip
                  label="Started"
                  size="small"
                  sx={{ height: 20, bgcolor: 'info.main', color: 'info.contrastText', '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem', fontWeight: 700 } }}
                />
                <Typography variant="caption" color="text.secondary">Started (Past)</Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Chip
                  label="Start"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem', fontWeight: 700 } }}
                />
                <Typography variant="caption" color="text.secondary">Starts Today</Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Chip
                  label="Ending"
                  size="small"
                  color="error"
                  variant="outlined"
                  sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem', fontWeight: 700 } }}
                />
                <Typography variant="caption" color="text.secondary">Ends Today</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Chip
                  label="Backlog"
                  size="small"
                  sx={{ height: 20, bgcolor: 'warning.main', color: 'warning.contrastText', '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem', fontWeight: 700 } }}
                />
                <Typography variant="caption" color="text.secondary">Overdue</Typography>
              </Box>
            </Box>
          </Paper>
        )}

        <EventDetailModal event={selectedEvent} open={isModalOpen} onClose={handleModalClose} timezone={timezone} selectedDate={selectedDate} currentView={currentView} />

        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">{snackbar.message}</Alert>
        </Snackbar>
      </Box>

      <style>{`
        ${spinAnimation}
        .fc-event { cursor: pointer !important; font-size: ${isMobile ? '0.7rem' : '0.8rem'} !important; padding: 2px 6px !important; }
        .fc-daygrid-event-dot { display: none !important; }
        .fc-daygrid-day-number { font-weight: ${theme.typography.fontWeightMedium} !important; color: ${theme.palette.text.primary} !important; }
        .fc-day-today { background: ${`${theme.palette.primary.main}10`} !important; }
        .fc-day-today .fc-daygrid-day-number { color: ${theme.palette.primary.main} !important; font-weight: ${theme.typography.fontWeightBold} !important; }
        .fc-col-header-cell { background: ${theme.palette.background.default} !important; padding: 8px 0 !important; }
        .fc-col-header-cell-cushion { font-weight: ${theme.typography.fontWeightMedium} !important; color: ${theme.palette.text.secondary} !important; }
        .fc-daygrid-day-frame { min-height: ${isMobile ? '80px' : '100px'} !important; }
        .fc-dayGridDay-view .fc-daygrid-day { min-height: 120px !important; }
        @media (max-width: 600px) { .fc-toolbar { flex-direction: column !important; gap: 8px !important; } .fc-toolbar-title { font-size: 1.1rem !important; } }
      `}</style>
    </DashboardLayout>
  );
}